import { clsx } from "clsx";
import { type FC, lazy, Suspense, useEffect, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { type Location, matchPath, Outlet, useLocation, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { LoadingSpinner, NotificationContainer, usePrevious } from "widgets";
import { useHotKeys } from "widgets/hooks";

import cssStyle from "@/App.module.css";
import { viewContextPathMatch } from "@/app-utils/sustain-view-context.ts";
import { CLEAR_SAVED_VIEW, SET_TABLE_CONTEXT } from "@/redux/action-types.ts";
import { isDetailPage, TableType } from "@/redux/modules/table-context.ts";
import { ConfirmationServiceProvider } from "@/services/ConfirmationServiceProvider.tsx";
import { DataFetchingError } from "@/ui-modules/core/errors/DataFetchingError.js";
import { LicenseCheck } from "@/ui-modules/core/errors/LicenseCheck.tsx";
import { RuntimeException } from "@/ui-modules/core/errors/RuntimeException.tsx";
import { Introduction } from "@/ui-modules/core/Introduction.js";
import { NavBar } from "@/ui-modules/core/navbar/Navbar.js";
import { ModalControlledOverlay } from "@/ui-modules/core/overlays/index.js";
import style from "@/ui-modules/core/styles/App.module.scss";
import shortcutsStyle from "@/ui-modules/help/styles/Shortcuts.module.scss";

import WebApiClient from "./api/WebApiClient.js";
import { FullscreenContext } from "./app_context.js";
import { BrowserSupportLevel, browserSupportLevel, browserWarning } from "./app-utils/browser-compatibility.js";
import { type User } from "./graphql.js";
import { auditRouteChange, updateLocalUserPref } from "./redux/modules/auth.js";
import { type IAppState } from "./redux/reducer.js";
import { type ReduxDispatch } from "./redux/redux-utils.js";
import { Hotkeys } from "./settings/hotkeys.js";
import { ALERTS_QUEUE_CONTAINER_ID, DISCONNECTION_QUEUE_CONTAINER_ID } from "./settings/settings.js";

// Dynamic Components
const Shortcuts = lazy(() => import("@/ui-modules/help/Shortcuts.tsx"));

export const AppLayout: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const location: Location = useLocation();
  const [previousLocation, setPreviousLocation] = useState<Location>();
  const navigate = useNavigate();
  const session = useSelector<IAppState, User>((state) => state.auth.session);
  const showIntro = useMemo(() => session?.params?.firstLogin, [session]);
  const isLoadingAppInfo = useSelector<IAppState, boolean>((state) => state.config.isLoadingAppInfo);

  const fullscreenState = useState(false);

  // Global Keyboard Shortcuts Help Dialog
  const showShortcuts = useSelector<IAppState, boolean>(
    (state) => state.auth.localUserPref?.showShortcutsHelp,
    shallowEqual,
  );
  useHotKeys(Hotkeys.navBar.showShortcuts, () => dispatch(updateLocalUserPref({ showShortcutsHelp: true })));

  useEffect(() => {
    if (browserSupportLevel === BrowserSupportLevel.PARTLY) {
      dispatch(browserWarning());
    } else if (browserSupportLevel === BrowserSupportLevel.UNSUPPORTED) {
      navigate("/unsupported");
    }
  }, []);

  // route change
  useEffect(() => {
    // Logger
    dispatch(auditRouteChange(previousLocation, location));
    setPreviousLocation(location);
  }, [dispatch, location]);

  useTableContextBrowsing();

  useSavedViewContextKeeper();

  return (
    <>
      <ConfirmationServiceProvider>
        <FullscreenContext.Provider value={fullscreenState}>
          {/* Separate container for the sequence analyzer to account for style interferences */}
          <div className={clsx(style.sequenceAnalyzer, cssStyle.app)} id={"sequence-analyzer-module"} />
          {/* Hide main app when sequence analyzer is opened to account for style interferences */}
          <div
            className={clsx(style.app, cssStyle.app, "root")}
            style={(location.pathname.match("/visualizer/sequence?sessionId") ||
              location.pathname.match("/visualizer/sequence/?sessionId")) ? { height: "unset" } : undefined}
          >
            <NavBar style={location.pathname.match("/visualizer/sequence") ? { display: "none" } : undefined} />
            {isLoadingAppInfo ? (
              <div style={{ gridArea: "page" }}>
                <LoadingSpinner inCenter />
              </div>
            ) : (
              // prettier-ignore
              <ErrorBoundary FallbackComponent={RuntimeException}>
                <LicenseCheck>
                  <DataFetchingError>
                    <Outlet />
                  </DataFetchingError>
                </LicenseCheck>
              </ErrorBoundary>
            )}
          </div>
        </FullscreenContext.Provider>
      </ConfirmationServiceProvider>

      <NotificationContainer containerId={ALERTS_QUEUE_CONTAINER_ID} />
      <NotificationContainer containerId={DISCONNECTION_QUEUE_CONTAINER_ID} position="top-right" />

      {showIntro && (
        <ModalControlledOverlay
          close={() => {
            /* empty */
          }}
          isOpen
        >
          {() => <Introduction />}
        </ModalControlledOverlay>
      )}

      {showShortcuts && (
        <Suspense fallback={<div className={shortcutsStyle.container} />}>
          <Shortcuts />
        </Suspense>
      )}

      <iframe
        title="Download"
        src=""
        style={{ display: "none" }}
        name={WebApiClient.DOWNLOAD_IFRAME_TARGET_NAME}
        id={WebApiClient.DOWNLOAD_IFRAME_TARGET_NAME}
      />
    </>
  );
};

const useTableContextBrowsing = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname;
    if (matchPath("/search/*", pathname)) {
      // record the search result context
      dispatch({ type: SET_TABLE_CONTEXT, payload: TableType.SearchResults });
    } else if (matchPath("/group/*", pathname)) {
      // record the saved group context
      dispatch({ type: SET_TABLE_CONTEXT, payload: TableType.SavedGroup });
    } else if (!isDetailPage(pathname)) {
      // for all routes except all detail pages, we clear the table context
      dispatch({ type: SET_TABLE_CONTEXT, payload: TableType.None });
    }
  }, [location.pathname]);
};

const useSavedViewContextKeeper = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const location: Location = useLocation();
  const previousLocation = usePrevious(location);
  const [searchParams, setSearchParams] = useSearchParams();
  const previousSearchParams = usePrevious(searchParams);

  useEffect(() => {
    // if match is not null it means the previous location might be candidate for view context sustainibility
    const match = previousLocation && viewContextPathMatch(previousLocation);
    // ...  if viewid was there before AND if it does not appear in the new location
    if (match && !searchParams.has("viewid") && previousSearchParams?.has("viewid")) {
      // ... only if the variable (e.g. job id) is the same! (since we could push a different notification and stay in the same component)
      if (matchPath({ path: match.pathnameBase, end: false }, location.pathname)) {
        // .. inject the search piece  (e.g. ?viewid=123)

        // When navigating from the Synteny Viewer to another tab of the summary page, if a feature is already "loaded" then clear the url params of the features' data
        for (const paramToRemove of ["organismId", "entityId", "resultType", "resultSubTypeId"]) {
          if (previousSearchParams.has(paramToRemove)) {
            previousSearchParams?.delete(paramToRemove);
          }
        }

        setSearchParams(previousSearchParams, { replace: true });
      } else {
        // if the path does not match we can clear the saved view information, so that we force to fully reload next time (e.g. SEL6-2008, changing the reference genome) ;
        // because of SEL6-2508 we do not dispatch directly a state reset from here, we only change the action and accounts for the saved-view module to behave accordingly
        dispatch({ type: CLEAR_SAVED_VIEW });
      }
    }
  }, [location.pathname]);
};
